/**
 * Build a Zod validation schema dynamically from backend connector field definitions.
 *
 * This replaces hardcoded per-connector validation logic with a single generic
 * builder that works for any connector type.
 */

import { z } from 'zod';
import type { ConnectorFieldSchema } from '../../types/connector-schema';
import { safeRegexTest } from './safeRegex';

/**
 * Build a Zod schema from an array of field definitions.
 * Only visible fields are validated; invisible fields get z.any().optional().
 *
 * @param fields - Field definitions from the backend schema
 * @param visibleFields - Set of field names that are currently visible
 * @returns A Zod object schema for form validation
 */
export function buildZodSchema(
  fields: ConnectorFieldSchema[],
  visibleFields: Set<string>
): z.ZodObject {
  const shape: Record<string, z.ZodType> = {};

  for (const field of fields) {
    if (!visibleFields.has(field.name)) {
      shape[field.name] = z.any().optional();
      continue;
    }

    shape[field.name] = buildFieldSchema(field);
  }

  return z.object(shape);
}

function buildFieldSchema(field: ConnectorFieldSchema): z.ZodType {
  let schema: z.ZodType;

  switch (field.type) {
    case 'number':
      schema = buildNumberSchema(field);
      break;
    case 'boolean':
      schema = z.boolean().optional().default(
        typeof field.default === 'boolean' ? field.default : false
      );
      break;
    case 'multi_select_checkbox':
      schema = buildArraySchema(field, z.string());
      break;
    case 'multi_select_checkbox_dynamic':
      // Loader-backed multi-select stores whatever primitive the loader
      // returns (e.g. numeric advertiser IDs). Accept any primitive item.
      schema = buildArraySchema(field, z.any());
      break;
    case 'oauth_button':
    // `oauth_populated_dropdown` stays permissive ON PURPOSE (#1013). Its
    // `required` rule cannot be expressed here: on an `auto_select` field an
    // empty array is the adapters' "extract everything" sentinel (#1014), so
    // `required` + empty is VALID there and invalid only on a required
    // allow-list (`multi` with no `auto_select`). Re-deriving that distinction here would duplicate it.
    // Since #1163 BOTH surfaces compile this builder — the wizard, and the detail
    // config editor via SectionedConfigurationForm (which scopes it to
    // `dirty ∩ visible`) — so a rule added here would have to be correct for both.
    // Enforcement therefore lives in ONE place they already share: the
    // DiscoveryGate's `blocked` registry, reported by OAuthPopulatedDropdownField
    // and read by the wizard's Continue button and the editor's SaveBar.
    // See lib/oauth/discoveryGate.tsx.
    case 'oauth_populated_dropdown':
    case 'hidden':
      schema = z.any().optional();
      break;
    case 'file':
      schema = field.required
        ? z.any().refine((val) => val != null, { message: `${field.label} is required` })
        : z.any().optional();
      break;
    case 'text':
    case 'password':
    case 'textarea':
    case 'select':
    // `select_dynamic` stores the same scalar string a `select` does -- its
    // options just arrive from a loader instead of the schema (#1999), which
    // validation does not care about.
    case 'select_dynamic':
    default:
      schema = buildStringSchema(field);
      break;
  }

  return schema;
}

function buildStringSchema(field: ConnectorFieldSchema): z.ZodType {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, { message: `${field.label} is required` });
  }

  // Apply validation rules
  for (const rule of field.validations) {
    switch (rule.type) {
      case 'min':
        if (typeof rule.value === 'number') {
          schema = schema.min(rule.value, { message: rule.message });
        }
        break;
      case 'max':
        if (typeof rule.value === 'number') {
          schema = schema.max(rule.value, { message: rule.message });
        }
        break;
    }
  }

  // Pattern validation using safeRegex
  const patternRule = field.validations.find((r) => r.type === 'pattern');
  if (patternRule && typeof patternRule.value === 'string') {
    const pattern = patternRule.value;
    const message = patternRule.message;
    // Use refine with safeRegexTest for ReDoS-safe pattern matching
    let refined: z.ZodType = schema.refine(
      (val: string) => !val || safeRegexTest(pattern, val),
      { message }
    );
    if (!field.required) {
      refined = z.union([refined, z.literal('')]).optional();
    }
    return refined;
  }

  if (!field.required) {
    return schema.optional().or(z.literal(''));
  }

  return schema;
}

function buildArraySchema(
  field: ConnectorFieldSchema,
  item: z.ZodType,
): z.ZodType {
  let schema = z.array(item);

  if (field.required) {
    schema = schema.min(1, { message: `${field.label} is required` });
  }

  for (const rule of field.validations) {
    if (typeof rule.value !== 'number') continue;
    if (rule.type === 'min') {
      schema = schema.min(rule.value, { message: rule.message });
    } else if (rule.type === 'max') {
      schema = schema.max(rule.value, { message: rule.message });
    }
  }

  if (!field.required) {
    return schema.optional().default([]);
  }
  return schema;
}


/**
 * Numeric bounds come from `min_value` / `max_value` ONLY.
 *
 * `field.validations` min/max rules are deliberately NOT read here (#1149).
 * The sibling builders read them as LENGTH bounds (string length, array
 * length); on a number the natural reading would be a value bound, but
 * declaring one that way would still be wrong: `min_value`/`max_value` is the
 * one channel every reader shares -- this builder (now called by BOTH the wizard
 * and the detail configuration form, #1163) and the HTML min/max attributes
 * emitted by NumberField and the detail form's FieldRenderer.
 *
 * The backend enforces that: `ConnectorFieldSchema` raises at import when a
 * NUMBER field carries a min/max ValidationRule
 * (`connectors/api/phase3_models.py::_reject_numeric_bound_rules`), so the
 * combination cannot arrive from the API. (A characterization test just below
 * still feeds it to this function directly, to pin the builder's own behaviour.)
 *
 * #1229 widened that backend guard to every channel this function ignores --
 * pattern/custom rules and the string-shaped min_length/max_length/pattern keys
 * -- and mirrored it onto `ConnectorFieldSchemaResponse`. Nothing changed here:
 * reading exactly one channel is the intended behaviour, and the guard exists so
 * that the one channel is the only one anybody can declare.
 */
function buildNumberSchema(field: ConnectorFieldSchema): z.ZodType {
  let schema = z.coerce.number();

  if (field.min_value != null) {
    schema = schema.min(field.min_value, {
      message: `Minimum value is ${field.min_value}`,
    });
  }
  if (field.max_value != null) {
    schema = schema.max(field.max_value, {
      message: `Maximum value is ${field.max_value}`,
    });
  }

  if (!field.required) {
    return schema.optional();
  }

  return schema;
}
